import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Eye, EyeOff, ArrowRight, ArrowLeft } from "lucide-react";
import { useAppContext } from "@/providers";
import { Input } from "@/components/ui/input";
import { validateLogin } from "@/lib/auth/login-schema";
import { performLogin } from "@/lib/auth/session-store";
import { t } from "@/lib/i18n/messages";
import { cn } from "@/lib/utils";

export function LoginForm() {
  const { locale, setSession } = useAppContext();
  const navigate = useNavigate();
  const isRTL = locale === "ar";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGlobalError("");

    const parsed = validateLogin({ email, password });
    if (!parsed.success) {
      setFieldErrors({
        email:
          parsed.errors?.email === "invalid"
            ? t(locale, "invalidEmail")
            : parsed.errors?.email
              ? t(locale, "requiredField")
              : "",
        password: parsed.errors?.password ? t(locale, "requiredField") : "",
      });
      return;
    }

    setFieldErrors({});
    setSubmitting(true);

    if (!parsed.data) {
      setSubmitting(false);
      return;
    }

    try {
      const sessionState = await performLogin(parsed.data);
      setSession(sessionState);
      if (sessionState.isAuthenticated) {
        navigate("/dashboard", { replace: true });
      } else {
        setGlobalError(sessionState.errorMessage || t(locale, "loginFailed"));
      }
    } catch {
      setGlobalError(t(locale, "loginFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5" noValidate>
      <Input
        id="email"
        name="email"
        type="email"
        inputMode="email"
        autoComplete="email"
        dir="ltr"
        required
        label={t(locale, "email")}
        placeholder="partner.user@ncircle.ai"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={submitting}
        error={fieldErrors.email}
      />

      <Input
        id="password"
        name="password"
        type={showPassword ? "text" : "password"}
        autoComplete="current-password"
        required
        label={t(locale, "password")}
        placeholder="••••••••"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        disabled={submitting}
        error={fieldErrors.password}
        trailingIcon={
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="hover:text-foreground transition-colors"
            aria-label={showPassword ? "Hide password" : "Show password"}
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4" strokeWidth={1.8} />
            ) : (
              <Eye className="w-4 h-4" strokeWidth={1.8} />
            )}
          </button>
        }
      />

      <div className="flex justify-end -mt-1">
        <Link
          to="/forgot-password"
          className="font-sans text-[10px] tracking-widest text-foreground-secondary hover:text-foreground transition-colors uppercase"
        >
          {t(locale, "forgotPassword")}
        </Link>
      </div>

      {globalError && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 px-4 py-3">
          <p className="font-sans text-[11px] tracking-widest text-red-500 uppercase">
            {globalError}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className={cn(
          "w-full h-12 inline-flex items-center justify-center gap-2",
          "bg-primary hover:bg-primary-dark text-white rounded-pill",
          "font-bold text-[15px] shadow-sm",
          "transition-all active:scale-[0.98]",
          "disabled:opacity-60 disabled:cursor-not-allowed",
        )}
      >
        {submitting ? (
          <span className="inline-flex items-center gap-2">
            <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
            <span>{t(locale, "login")}</span>
          </span>
        ) : (
          <>
            <span>{t(locale, "login")}</span>
            {isRTL ? (
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            ) : (
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
            )}
          </>
        )}
      </button>
    </form>
  );
}
